import { useEffect, useState } from 'react';
import { Edit, Plus } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { Collaborator, mensagemDeErro } from '../api';
import { Allocation, AllocationPayload } from '../api/alocacoes';
import {
  useAlocacoes, useAlocar, useAtualizarAlocacao, useClientesParaAlocacao, useEncerrarAlocacao, useOpcoesDeAlocacao,
} from '../hooks/alocacoes';
import { formatDate, isAllocationActive } from './format';
import { PessoasModal } from './PessoasModal';

const hoje = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());

/**
 * Alocações do colaborador em clientes (change `pessoas-ferias-gestor-cliente`):
 * definem quem o gestor do cliente acompanha no portal e as regras para as férias.
 */
export function AlocacoesTab({ person }: { person: Collaborator }) {
  const { can } = usePermissions();
  const bond = person.bonds.find((b) => !b.closed);
  const alocacoes = useAlocacoes(person.id);
  const [form, setForm] = useState<Allocation | 'nova' | null>(null);
  const [encerrando, setEncerrando] = useState<Allocation | null>(null);
  const lista = alocacoes.data ?? [];
  const today = hoje();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">O gestor do cliente acompanha as férias dos profissionais alocados com ele no portal do cliente.</p>
        {can(FEATURES.pessoasAlocacoes, 'create') && bond && (
          <Button size="sm" onClick={() => setForm('nova')}><Plus className="mr-1 h-4 w-4" /> Nova alocação</Button>
        )}
      </div>

      {alocacoes.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : lista.length === 0 ? (
        <EmptyState size="compact" title="Nenhuma alocação em cliente" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {lista.map((a) => {
            const ativa = isAllocationActive(a, today) || a.validFrom > today;
            return (
              <li key={a.id} className={`flex flex-wrap items-start justify-between gap-2 px-3 py-3 text-sm ${ativa ? '' : 'opacity-60'}`}>
                <div className="space-y-0.5">
                  <p className="font-medium text-gray-900">
                    {a.client.name}{a.project && <span className="font-normal text-gray-600"> · {a.project.name}</span>}
                  </p>
                  <p className="text-gray-700">
                    {formatDate(a.validFrom)} a {a.validTo ? formatDate(a.validTo) : 'atual'}
                    {a.professionalProfile && ` · ${a.professionalProfile.name}`}
                  </p>
                  <p className="text-xs text-gray-600">
                    Gestor do cliente: {a.clientManager.name}{!a.clientManager.active && ' (acesso inativo)'}
                    {a.requiresSubstitute && ' · exige substituto nas férias'}
                    {a.suspendAccess && ` · suspende acessos nas férias${a.accessNotes ? ` (${a.accessNotes})` : ''}`}
                  </p>
                  {a.endedReason && <p className="text-xs text-gray-500">Encerrada: {a.endedReason}</p>}
                </div>
                {ativa && can(FEATURES.pessoasAlocacoes, 'edit') && (
                  <span className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setForm(a)}><Edit className="mr-1 h-4 w-4" /> Editar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEncerrando(a)}>Encerrar</Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {bond && <AlocacaoModal bondId={bond.id} value={form} onClose={() => setForm(null)} />}
      <EncerrarModal alocacao={encerrando} onClose={() => setEncerrando(null)} />
    </div>
  );
}

function AlocacaoModal({ bondId, value, onClose }: { bondId: string; value: Allocation | 'nova' | null; onClose: () => void }) {
  const toast = useToast();
  const editando = value && value !== 'nova' ? value : null;
  const alocar = useAlocar(bondId);
  const atualizar = useAtualizarAlocacao();
  const clientes = useClientesParaAlocacao(value === 'nova');
  const [clientId, setClientId] = useState('');
  const opcoes = useOpcoesDeAlocacao(editando?.client.id ?? clientId);
  const [form, setForm] = useState<AllocationPayload>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!value) return;
    setError(null);
    if (editando) {
      setClientId(editando.client.id);
      setForm({
        clientManagerUserId: editando.clientManager.id, professionalProfileId: editando.professionalProfile?.id ?? null,
        requiresSubstitute: editando.requiresSubstitute, suspendAccess: editando.suspendAccess, accessNotes: editando.accessNotes ?? '',
      });
    } else {
      setClientId('');
      setForm({ validFrom: hoje(), requiresSubstitute: false, suspendAccess: false });
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof AllocationPayload>(k: K, v: AllocationPayload[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    setError(null);
    if (!editando && !clientId) return setError('Escolha o cliente.');
    if (!form.clientManagerUserId) return setError('Escolha o gestor do cliente.');
    if (!editando && !form.validFrom) return setError('Informe o início.');
    if (form.suspendAccess && !form.accessNotes?.trim()) return setError('Descreva os acessos a suspender.');
    try {
      if (editando) {
        await atualizar.mutateAsync({
          id: editando.id,
          payload: {
            clientManagerUserId: form.clientManagerUserId, professionalProfileId: form.professionalProfileId ?? null,
            requiresSubstitute: form.requiresSubstitute, suspendAccess: form.suspendAccess, accessNotes: form.accessNotes?.trim() || null,
          },
        });
        toast.success('Alocação atualizada.');
      } else {
        await alocar.mutateAsync({
          ...form, clientId, projectId: form.projectId || undefined, professionalProfileId: form.professionalProfileId || undefined,
          validTo: form.validTo || undefined, accessNotes: form.accessNotes?.trim() || undefined,
        });
        toast.success('Alocação registrada.');
      }
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar a alocação.'));
    }
  }

  const managers = opcoes.data?.managers ?? [];
  return (
    <PessoasModal open={value !== null} size="lg" title={editando ? `Alocação — ${editando.client.name}` : 'Nova alocação em cliente'}
      submitLabel="Salvar" isSubmitting={alocar.isPending || atualizar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {!editando && (
          <>
            <AutocompleteSelect label="Cliente" options={(clientes.data ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }))}
              value={clientId} onChange={(v) => { setClientId(v); setForm((f) => ({ ...f, projectId: undefined, clientManagerUserId: undefined })); }} />
            <AutocompleteSelect label="Projeto (opcional)" disabled={!clientId}
              options={[{ value: '', label: 'Sem projeto' }, ...(opcoes.data?.projects ?? []).map((p) => ({ value: p.id, label: p.name }))]}
              value={form.projectId ?? ''} onChange={(v) => set('projectId', v || undefined)} />
            <TextInput label="Início" type="date" value={form.validFrom ?? ''} onChange={(e) => set('validFrom', e.target.value)} />
            <TextInput label="Fim (opcional)" type="date" value={form.validTo ?? ''} onChange={(e) => set('validTo', e.target.value || undefined)} />
          </>
        )}
        <AutocompleteSelect label="Gestor do cliente" disabled={!clientId}
          options={managers.map((m) => ({ value: m.id, label: `${m.name} — ${m.email}` }))}
          value={form.clientManagerUserId ?? ''} onChange={(v) => set('clientManagerUserId', v)} />
        <AutocompleteSelect label="Perfil de atuação (opcional)"
          options={[{ value: '', label: 'Usar o cargo' }, ...(opcoes.data?.profiles ?? []).map((p) => ({ value: p.id, label: p.name }))]}
          value={form.professionalProfileId ?? ''} onChange={(v) => set('professionalProfileId', v || null)} />
      </div>
      {clientId && managers.length === 0 && !opcoes.isLoading && (
        <p className="text-xs text-amber-700">O cliente não tem acesso externo ativo: cadastre um em "Acessos externos" no cliente.</p>
      )}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.requiresSubstitute} onChange={(e) => set('requiresSubstitute', e.target.checked)} />
          Exige substituto durante as férias
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.suspendAccess} onChange={(e) => set('suspendAccess', e.target.checked)} />
          Suspender os acessos ao cliente durante as férias
        </label>
        {form.suspendAccess && (
          <Textarea label="Acessos a suspender (sistemas, VPN, crachá…)" value={form.accessNotes ?? ''} onChange={(e) => set('accessNotes', e.target.value)} />
        )}
      </div>
    </PessoasModal>
  );
}

function EncerrarModal({ alocacao, onClose }: { alocacao: Allocation | null; onClose: () => void }) {
  const toast = useToast();
  const encerrar = useEncerrarAlocacao();
  const [validTo, setValidTo] = useState(hoje());
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!alocacao) return;
    if (!reason.trim()) return setError('Informe o motivo.');
    try {
      await encerrar.mutateAsync({ id: alocacao.id, validTo, reason: reason.trim() });
      toast.success('Alocação encerrada.');
      setReason('');
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível encerrar a alocação.'));
    }
  }

  return (
    <PessoasModal open={alocacao !== null} title={`Encerrar alocação — ${alocacao?.client.name ?? ''}`} submitLabel="Encerrar"
      submitVariant="danger" isSubmitting={encerrar.isPending} submitError={error} onClose={onClose} onSubmit={submit}
      description="Tarefas de acesso previstas depois do fim são canceladas.">
      <TextInput label="Último dia" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
      <Textarea label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} />
    </PessoasModal>
  );
}
